import { ListBase, useListContext } from "ra-core";
import { Link, useNavigate } from "react-router";

import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { usePermission } from "../providers/usePermission";
import { CompanyCreateDialog } from "./CompanyEditor";
import { Building2 } from "lucide-react";

import { EmptyState, GraphError, GraphLoading } from "./GraphStatus";
import { ListPage, RowAvatar } from "./record/ListPage";
import { RECORD_LINK } from "./record/styles";

interface GraphCompany {
  id: string;
  name: string;
  kind_id: string | null;
  kind_label?: string | null;
  parent_company_id?: string | null;
  parent_name?: string | null;
  member_count?: number;
}

export function GraphCompanyList() {
  return (
    <ListBase perPage={200}>
      <CompanyRows />
    </ListBase>
  );
}

function CompanyRows() {
  const { data, total, isPending, error } = useListContext<GraphCompany>();
  const canCreate = usePermission("create_records");
  const navigate = useNavigate();
  if (isPending && !data) return <GraphLoading />;
  if (error) return <GraphError error={error} noun="company list" />;

  const rows = data ?? [];
  return (
    <ListPage
      title="Companies"
      count={total ?? rows.length}
      actions={canCreate ? <CompanyCreateDialog /> : null}
      footer={
        total != null && total > rows.length
          ? `Showing the first ${rows.length} of ${total}.`
          : null
      }
    >
      <Table>
        <TableHeader className="bg-muted/40">
          <TableRow>
            <TableHead className="pl-4">Name</TableHead>
            <TableHead>Kind</TableHead>
            <TableHead>Parent</TableHead>
            <TableHead className="pr-4 text-right">Members</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="p-0">
                <EmptyState icon={Building2} title="No companies yet." />
              </TableCell>
            </TableRow>
          ) : null}
          {rows.map((row) => (
            <TableRow
              key={row.id}
              className="cursor-pointer"
              onClick={(event) => {
                if ((event.target as HTMLElement).closest("a")) return;
                navigate(`/companies/${row.id}/show`);
              }}
            >
              <TableCell className="pl-4">
                <div className="flex items-center gap-3">
                  <RowAvatar name={row.name} square />
                  <Link
                    className={RECORD_LINK}
                    to={`/companies/${row.id}/show`}
                  >
                    {row.name}
                  </Link>
                </div>
              </TableCell>
              <TableCell>
                {row.kind_label ? (
                  <Badge variant="secondary" className="font-normal">
                    {row.kind_label}
                  </Badge>
                ) : null}
              </TableCell>
              <TableCell className="text-muted-foreground">
                {row.parent_company_id ? (
                  <Link
                    className={
                      RECORD_LINK + " font-normal text-muted-foreground"
                    }
                    to={`/companies/${row.parent_company_id}/show`}
                  >
                    {row.parent_name}
                  </Link>
                ) : null}
              </TableCell>
              <TableCell className="pr-4 text-right tabular-nums text-muted-foreground">
                {row.member_count ?? 0}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </ListPage>
  );
}
